import UpdateButton from "../../shared/UpdateButton/UpdateButton";
import {useEffect, useState} from "react";
import {
  ChimesTime,
  ClockChimeSettingsProps,
  FieldKey,
  IntervalKey,
  MODES,
  SCHEMES
} from "../../../models/ChimeSettingsSection.model";
import ClockChimeTimeInput from "./components/ClockChimeTimeInput";
import {
  Button,
  IconButton,
  Input,
  RadioTile,
  RadioTileGroup,
  Slider,
  toaster,
  Toggle,
  Tooltip,
  Whisper
} from "rsuite";
import {ClockChimeSettingsApi} from "../../../services/ClockChimeSettings.api";
import NotificationFactory from "../../../shared/Notifications";
import HelpOutlineIcon from "@rsuite/icons/HelpOutline";
import AdminComponent from "../../auth/AdminComponent/AdminComponent";
import {TfiSaveIcon} from "../../../models/Icons.model";

const ClockChimeSettings = ({deviceId, withCustomMusic, isAdmin}: ClockChimeSettingsProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [chimesTime, setChimesTime] = useState<ChimesTime>({first: {start: 0, end: 0}, second: {start: 0, end: 0}});
  const [scheme, setScheme] = useState<number>(0);
  const [mode, setMode] = useState<number>(0);
  const [christmasChimes, setChristmasChimes] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0);
  const [description, setDescription] = useState<string>("");

  useEffect(() => {
    getChimesParams().then();
    getDescription().then();
  }, [deviceId]);

  const getChimesParams = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await ClockChimeSettingsApi.getClockChimeParams(deviceId);

      if (response) {
        setChimesTime({
          first: {
            start: response.firstStartChimes,
            end: response.firstEndChimes
          },
          second: {
            start: response.secondStartChimes,
            end: response.secondEndChimes
          }
        });
        setMode(response.chimesMode);
        setScheme(response.chimesScheme);
        setChristmasChimes(response.christmasChimes);
        setVolume(response.chimesSoundLevel);
      }
    } catch (e) {
      console.error(e);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const updateChimesTime = async (): Promise<void> => {
    setLoading(true);
    try {
      const result = await ClockChimeSettingsApi.updateClockChimeTime(deviceId, chimesTime);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
        await getChimesParams();
      }
    } catch (e) {
      console.error(e);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const updateInterval = (interval: IntervalKey, field: FieldKey, hours: number): void => {
    setChimesTime(prev => ({
      ...prev,
      [interval]: {
        ...prev[interval],
        [field]: hours
      }
    }));
  };

  const updateChimeParams = async (
    nextMode: number,
    nextScheme: number,
    nextChristmas: boolean,
    nextVolume: number
  ): Promise<void> => {
    setLoading(true);

    const payload = {
      mode: nextMode,
      scheme: nextScheme,
      christmas: nextChristmas,
      chimesSoundLevel: nextVolume
    };

    try {
      const result = await ClockChimeSettingsApi.updateClockChimeParams(deviceId, payload);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });
        await getChimesParams();
      }
    } catch (err) {
      console.error(err);
      toaster.push(NotificationFactory.error(), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const getDescription = async (): Promise<void> => {
    if (!isAdmin) return 
    setLoading(true);

    try {
      const response = await ClockChimeSettingsApi.getClockChimeDescription(deviceId);

      if (response) {
        setDescription(response.technicalDescription);
      }
    } catch (err) {
      console.error(err);
      toaster.push(NotificationFactory.error("Не удалось получить описание"), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const updateDescription = async (): Promise<void> => {
    setLoading(true);

    const payload = {
      technicalDescription: description
    };

    try {
      const result = await ClockChimeSettingsApi.updateClockChimeDescription(deviceId, payload);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });

        await getDescription();
      }
    } catch (err) {
      console.error(err);
      toaster.push(NotificationFactory.error("Не удалось обновить описание"), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  const onSchemeChange = (newScheme: number): void => {
    updateChimeParams(mode, newScheme, christmasChimes, volume).then();
  };

  const onModeChange = (newMode: number): void => {
    updateChimeParams(newMode, scheme, christmasChimes, volume).then();
  };

  const onChristmasChange = (newChristmasChimes: boolean): void => {
    updateChimeParams(mode, scheme, newChristmasChimes, volume).then();
  };

  const onVolumeChange = (newVolume: number): void => {
    setVolume(newVolume);
  };

  const updateVolume = async (volume: number) => {
    setLoading(true);
    try {
      const result = await ClockChimeSettingsApi.updateClockChimeVolume(deviceId, volume);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });

        await getChimesParams();
      }
    } catch (err) {
      console.error(err);
      toaster.push(NotificationFactory.error("Не удалось обновить описание"), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }

  const clockChimeTest = async () => {
    setLoading(true);
    try {
      const result = await ClockChimeSettingsApi.clockChimeTest(deviceId);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });

        await getChimesParams();
      }
    } catch (err) {
      console.error(err);
      toaster.push(NotificationFactory.error("Не удалось обновить описание"), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }

  const clockLossTest = async () => {
    setLoading(true);
    try {
      const result = await ClockChimeSettingsApi.clockLossTest(deviceId);

      if (result) {
        toaster.push(NotificationFactory.success(), {
          placement: "topEnd",
          duration: 3000
        });

        await getChimesParams();
      }
    } catch (err) {
      console.error(err);
      toaster.push(NotificationFactory.error("Не удалось обновить описание"), {
        placement: "topEnd",
        duration: 3000
      });
    } finally {
      setLoading(false);
    }
  }


  return (
    <>
      {/* Время боя */}
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Время боя</div>
        <section className="secondary-clock">
          <div className="row p-2">
            <div className="col-12 d-flex justify-content-end gap-2">
              <Whisper placement="top" speaker={<Tooltip>Сохранить</Tooltip>}>
                <IconButton
                  style={{marginRight: "10px"}}
                  icon={<TfiSaveIcon />}
                  size="sm"
                  loading={loading}
                  onClick={updateChimesTime}
                  className="refresh-btn"
                />
              </Whisper>
              <UpdateButton loading={loading} onRefresh={updateChimesTime} />
            </div>
          </div>

          <div className="row p-2 pt-0">
            {/* 1 интервал */}
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
              <div className="fieldset-wrapper control h-100 bg-white mt-1 pt-2 ">
                <div className="legend-title" style={{height: 20}}>
                  1 интервал
                </div>
                <div className="row mt-3">
                  <div className="col-md-6 mb-2">
                    <ClockChimeTimeInput
                      label="Начало"
                      value={chimesTime.first.start}
                      onChange={hours => updateInterval("first", "start", hours)}
                    />
                  </div>
                  <div className="col-md-6">
                    <ClockChimeTimeInput
                      label="Конец"
                      value={chimesTime.first.end}
                      onChange={hours => updateInterval("first", "end", hours)}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* 2 интервал */}
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
              <div className="fieldset-wrapper control h-100 bg-white mt-0 pt-2">
                <div className="legend-title" style={{height: 20}}>
                  2 интервал
                </div>
                <div className="row mt-3">
                  <div className="col-md-6 mb-2">
                    <ClockChimeTimeInput
                      label="Начало"
                      value={chimesTime.second.start}
                      onChange={hours => updateInterval("second", "start", hours)}
                    />
                  </div>
                  <div className="col-md-6">
                    <ClockChimeTimeInput
                      label="Конец"
                      value={chimesTime.second.end}
                      onChange={hours => updateInterval("second", "end", hours)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Бой часов: схемы и режимы */}
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Бой часов</div>
        <section className="secondary-clock">
          <div className="row p-2">
            <div className="col-12 d-flex justify-content-end gap-2">
              <UpdateButton loading={loading} onRefresh={getChimesParams} />
            </div>
          </div>

          <div className="row p-2 pt-0">
            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
              <div className="fieldset-wrapper control h-100 bg-white mt-0 pt-2">
                <div className="legend-title" style={{height: 20}}>
                  Варианты боя
                </div>
                <div className="row mt-3">
                  <div className="col-md-12">
                    <RadioTileGroup
                      className="w-100"
                      aria-label="Chime scheme"
                      value={scheme}
                      onChange={value => onSchemeChange(value as number)}
                    >
                      {Object.entries(SCHEMES).map(([key, text]) => {
                        const numericKey = Number(key);
                        return (
                          <RadioTile
                            className="mb-0"
                            key={key}
                            value={numericKey}
                            label={text}
                          >
                            <span className="radio-tile-text">{text}</span>
                          </RadioTile>
                        );
                      })}
                    </RadioTileGroup>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xxl-6 col-lg-6 col-md-12 col-sm-12 mb-3">
              <div className="fieldset-wrapper bg-white mt-0 p-3 pt-2 pb-3">
                <div className="legend-title" style={{height: 20}}>
                  Режим боя
                </div>
                <div className="row mt-3">
                  <div className="col-md-12">
                    <RadioTileGroup
                      className="w-100"
                      aria-label="Chime mode"
                      value={mode}
                      onChange={value => onModeChange(value as number)}
                    >
                      {Object.entries(MODES).map(([key, text]) => {
                        const numericKey = Number(key);
                        return (
                          <RadioTile
                            className="mb-0"
                            key={key}
                            value={numericKey}
                            label={text}
                          >
                            <span className="radio-tile-text">{text}</span>
                          </RadioTile>
                        );
                      })}
                    </RadioTileGroup>
                  </div>
                </div>
              </div>
              <div
                style={{height: 'auto'}}
                className="fieldset-wrapper control bg-white mt-0 pt-2 pb-3 d-flex justify-content-center">
                <div className="legend-title">Новогодний бой</div>
                <div className="d-flex mt-3 justify-content-center">
                  <Toggle
                    color="blue"
                    checkedChildren="ON"
                    unCheckedChildren="OFF"
                    loading={loading}
                    checked={christmasChimes}
                    onChange={onChristmasChange}
                    style={{marginRight: 8}}
                  />
                  <span style={{marginRight: 8}}>
                    {christmasChimes ? "Включен" : "Выключен"}
                  </span>
                  <Whisper
                    placement="top"
                    trigger="hover"
                    speaker={
                      <Tooltip>
                        Куранты на Спасской башне московского Кремля.
                        <br />
                        Не зависит от режима боя и варианта боя
                      </Tooltip>
                    }
                  >
                    <span style={{cursor: "help", color: "#999"}}>
                      <HelpOutlineIcon />
                    </span>
                  </Whisper>
                </div>
              </div>
              <div
                style={{height: 'auto'}}
                className="fieldset-wrapper control bg-white mt-2 pb-3">
                <div className="legend-title">Тест</div>
                <div className="d-flex align-items-center justify-content-between">
                  <Button
                    appearance="ghost"
                    style={{minWidth: 130}}
                    type="button"
                    loading={loading}
                    disabled={loading}
                    onClick={clockChimeTest}
                  >
                    Тест боя
                  </Button>
                  <Button
                    appearance="ghost"
                    style={{minWidth: 130}}
                    type="button"
                    loading={loading}
                    disabled={loading}
                    onClick={clockLossTest}
                  >
                    Тест проигрыша
                  </Button>
                </div>
              </div>

            </div>
          </div>

        </section>
      </div>


      {/* Громкость, новогодний бой, тест */}
      <div className="fieldset-wrapper mb-2">
        <div className="legend-title main-legend-title">Громкость</div>
        <section className="secondary-clock">
          <div className="row p-2 mt-3 d-flex justify-content-center">
            <div className="col-xxl-12 col-lg-12 col-md-12 col-sm-12 mb-3">
              <div className="d-flex align-items-center justify-content-between justify-content-center">
                <div className="flex-grow-1 me-3">
                  <Slider
                    progress
                    min={0}
                    max={30}
                    step={1}
                    value={volume}
                    onChange={value => onVolumeChange(value as number)}
                    onChangeCommitted={value => updateVolume(value)}
                    handleStyle={{
                      borderRadius: 10,
                      color: "#fff",
                      fontSize: 12,
                      width: "70%",
                      height: 22
                    }}
                    tooltip={false}
                  />

                </div>
                <span>{volume ? Math.round((volume / 30) * 100) : 0} %</span>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Техническая информация */}
      <AdminComponent>
        {() => (
          <div className="fieldset-wrapper mb-2">
            <div className="legend-title main-legend-title">Техническая информация</div>
            <section className="secondary-clock">
              <div className="row p-2">
                <div className="col-12 d-flex justify-content-end gap-2">
                  <Whisper placement="top" speaker={<Tooltip>Сохранить</Tooltip>}>
                    <IconButton
                      style={{marginRight: "10px"}}
                      icon={<TfiSaveIcon />}
                      size="sm"
                      loading={loading}
                      onClick={updateDescription}
                      className="refresh-btn"
                    />
                  </Whisper>
                  <UpdateButton loading={loading} onRefresh={getDescription} />
                </div>
              </div>
              <div className="row p-2 pt-0">
                <div className="col-md-12">
                  <Input
                    as="textarea"
                    rows={5}
                    value={description}
                    onChange={(val: string) => setDescription(val)}
                    className="p-2"
                    disabled={loading}
                  />
                </div>
              </div>
            </section>
          </div>
        )}
      </AdminComponent>
    </>
  );
};

export default ClockChimeSettings;
